import { Stack, Typography } from '@mui/material';
import color from '@theme/Colors';

/**
 * BE logs EV permit zones as one label: "Zone A (REF1), Zone B (REF2)".
 * Split it back into items; each item ends with ")" so split on "), ".
 */
export const parseEvPermitZonesLabel = (value: unknown): string[] => {
    if (typeof value !== 'string' || !value.trim() || value === 'N/A') return [];
    const parts = value.split('), ');
    return parts.map((part, index) => (index < parts.length - 1 ? `${part})` : part)).map((p) => p.trim());
};

type Props = {
    label: string;
    oldValue: unknown;
    newValue: unknown;
};

/**
 * Event-log line for "EV permit zones": the new list, one zone per line.
 * Newly added zones are marked "+" (green); zones removed from the old list follow, marked "−" (red).
 */
export default function EvPermitZonesChange({ label, oldValue, newValue }: Props) {
    const oldZones = parseEvPermitZonesLabel(oldValue);
    const newZones = parseEvPermitZonesLabel(newValue);
    const removed = oldZones.filter((zone) => !newZones.includes(zone));

    return (
        <ul style={{ marginTop: '4px', marginBottom: '4px' }}>
            <li>
                <Typography>
                    {label} ({oldZones.length} → {newZones.length}):
                </Typography>
                <Stack pl={2} mt={0.5} spacing={0.25}>
                    {newZones.length === 0 && removed.length === 0 && <Typography>N/A</Typography>}
                    {newZones.map((zone) => {
                        const isAdded = !oldZones.includes(zone);
                        return (
                            <Stack key={`new-${zone}`} direction="row" spacing={1}>
                                <Typography width={12} flexShrink={0} color={color.success}>
                                    {isAdded ? '+' : ''}
                                </Typography>
                                <Typography color={isAdded ? color.success : undefined}>{zone}</Typography>
                            </Stack>
                        );
                    })}
                    {removed.map((zone) => (
                        <Stack key={`removed-${zone}`} direction="row" spacing={1}>
                            <Typography width={12} flexShrink={0} color={color.danger}>
                                −
                            </Typography>
                            <Typography color={color.danger} sx={{ textDecoration: 'line-through' }}>
                                {zone}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </li>
        </ul>
    );
}
